import { CircularProgress } from '@material-ui/core';
import { Theme, ToastProvider } from '@zinc/ui';
import { SingletonHotkeysProvider } from '@zinc/ui/original-src/hooks/_private/useSingletonHotkeys';
import type React from 'react';
import { cloneElement } from 'react';
import { createRoot } from 'react-dom/client';
import { cache } from 'tm3-core/cache';
import NewModal, { DialogType, Result } from '../modal-new/confirmation/stores';
import type { IConfirmationProps } from './controls/confirmation';
import Confirmation from './controls/confirmation';

const getActiveReshapedThemeName = () => {
  if (typeof document === 'undefined') {
    return null;
  }

  const rootThemeEl = document.querySelector('[data-rs-root][data-rs-theme]');
  if (rootThemeEl) {
    return rootThemeEl.getAttribute('data-rs-theme');
  }

  return null;
};

const ModalThemeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const themeName = getActiveReshapedThemeName() ?? cache?.productName ?? 'TM3';

  return (
    <SingletonHotkeysProvider>
      <ToastProvider>
        <Theme name={themeName} scoped>
          {children}
        </Theme>
      </ToastProvider>
    </SingletonHotkeysProvider>
  );
};

class Modal {
  static create(control: React.ReactElement) {
    const wrapper = document.createElement('div');
    document.body.appendChild(wrapper);

    const root = createRoot(wrapper);

    return new Promise((resolve, reject) => {
      const cleanup = () =>
        setTimeout(() => {
          root.unmount();
          wrapper.remove();
        }, 2000); // leave DOM up for animation fade-out

      const withRef = cloneElement(control, {
        ref: (comp: any) => {
          if (comp?.promise) {
            comp.promise
              .then((res: any) => {
                cleanup();
                resolve(res);
              })
              .catch(() => {
                cleanup();
                reject();
              });
          }
        }
      });

      root.render(<ModalThemeProvider>{withRef}</ModalThemeProvider>);
    });
  }

  static confirmation(props: IConfirmationProps) {
    return this.create(<Confirmation {...props} />);
  }

  static alert(props: IConfirmationProps) {
    return this.create(<Confirmation {...props} alert={true} />);
  }

  static yesNoCancel(props: IConfirmationProps) {
    return this.create(<Confirmation {...props} yesno={true} defaultFocus="yes" />);
  }

  static spinner(props: Partial<IConfirmationProps>) {
    return this.create(
      <Confirmation
        {...props}
        type="spinner"
        description={
          <div style={{ display: 'flex' }}>
            <CircularProgress size={70} style={{ marginLeft: 'auto', marginRight: 'auto' }} />
          </div>
        }
      />
    );
  }

  static unsavedChanges(hasChanges: boolean, save?: () => void, close?: () => void) {
    const _save = save || (() => {});
    const _close = close || (() => {});

    if (!hasChanges) {
      _close();
      return Promise.resolve(false);
    }

    const promise = NewModal.open(DialogType.saveCheck);

    promise
      .then((result: Result) => {
        switch (result) {
          case Result.yes:
            _save();
            break;
          case Result.no:
            _close();
            break;
          case Result.cancel:
            break;
          default:
            throw new Error('Unexpected result from unsavedChanges modal');
        }
      })
      .catch((e) => {
        console.error('Error in unsavedChanges modal:', e);
      });
  }
}

export default Modal;
